import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { SketchPicker } from 'react-color';

import { useTownAssets } from '../../stores';
import { displayJson } from '../../utils';
import { Button } from '../buttons';
import {
  CardTitle,
  Column,
  Divider,
  FullPopup,
  Row,
} from '../display';

import ColorPickerButton from './ColorPickerButton';

const componentToHex = (n) => Number(n ?? 0).toString(16).padStart(2, '0');

const colorToHex = (color) => {
  if (!color) return '';
  if (typeof color === 'string') {
    let s = (color.startsWith('#')) ? color.slice(1) : color;
    if (s.length === 8) s = s.slice(0, 6);
    return s.toUpperCase();
  }

  return `${componentToHex(color.r)}${componentToHex(color.g)}${componentToHex(color.b)}`.toUpperCase();
};

const sanitizeHexInput = (raw) => {
  let s = (raw ?? '').toUpperCase();
  if (s.startsWith('#')) s = s.slice(1);
  s = s.replace(/[^0-9A-F]/g, '');
  if (s.length === 8) s = s.slice(0, 6);
  if (s.length > 6) s = s.slice(0, 6);
  return s;
};

function ColorPicker(props) {
  const {
    id,
    initialColor,
    colorPickingDone,
    disabled,
    canBeCleared,
    displayHexInput = false,
    label,
    suggested,
  } = props;
  const [popupVisible, setPopupVisible] = useState(false);
  const [colorPicked, setColorPicked] = useState(initialColor);
  const [hexText, setHexText] = useState(() => colorToHex(initialColor));

  const suggestedPaletteRows = (suggested?.palettes ?? []).filter((row) => (row?.colors?.length));
  const hasPaletteRows = suggestedPaletteRows.length > 0;
  const previewImgUrl = suggested?.imgUrl;
  const { assets } = useTownAssets();
  const {
    translate,
  } = assets;

  useEffect(
    () => { setHexText(colorToHex(initialColor)); },
    [initialColor],
  );

  const handleHexInputChange = (event) => {
    const next = sanitizeHexInput(event.currentTarget.value);
    setHexText(next);

    if (next.length === 6) {
      if (colorPickingDone) {
        colorPickingDone({
          id,
          color: `#${next}`,
        });
      }
    } else if ((next.length === 0) && canBeCleared) {
      if (colorPickingDone) {
        colorPickingDone({
          id,
          color: undefined,
        });
      }
    }
  };

  const handleHexInputBlur = () => {
    const isCommitted = (hexText.length === 6) || ((hexText.length === 0) && canBeCleared);
    if (!isCommitted) setHexText(colorToHex(initialColor));
  };

  const handleClick = () => {
    setPopupVisible((prev) => {
      if (!prev) {
        setColorPicked(initialColor);

        if (disabled) return prev;
      }

      return (!prev);
    });
  };

  const done = () => {
    if (colorPickingDone) {
      colorPickingDone({
        id,
        color: colorPicked,
      });
    }
    setPopupVisible(false);
  };

  const cancel = () => {
    setPopupVisible(false);
  };

  const clear = () => {
    setColorPicked();
  };

  const handleChange = (aNewColor) => {
    setColorPicked((currentColorPicked) => {
      if (currentColorPicked?.r !== undefined) {
        return aNewColor.rgb;
      }

      return aNewColor.hex;
    });
  };

  const handleChangeComplete = (aNewColor) => {
    console.log(`New color = ${displayJson(aNewColor)}`);
  };

  let buttonColor;
  if (initialColor) {
    if (initialColor.r) buttonColor = `rgba(${initialColor.r}, ${initialColor.g}, ${initialColor.b}, ${initialColor.a})`;
    else buttonColor = initialColor;
  }

  let previewBg = '#FFFFFF';
  if (colorPicked) {
    if (colorPicked.r !== undefined) {
      previewBg = `rgba(${colorPicked.r}, ${colorPicked.g}, ${colorPicked.b}, ${colorPicked.a})`;
    } else {
      previewBg = colorPicked;
    }
  }

  const hexInputId = (id) ? `${id}_hex` : undefined;

  const swatch = (
    <ColorPickerButton
      color={buttonColor}
      disabled={disabled}
      onClick={handleClick}
      tabIndex={(displayHexInput) ? -1 : undefined}
    />
  );

  return (
    <div className="relative inline-flex flex-row items-center">
      {(displayHexInput)
        ? (
            <div
              className={classNames(
                'relative flex flex-row items-center gap-2 rounded-md border border-pxMainOutline bg-white px-2 py-2',
                { 'opacity-80': disabled },
              )}
            >
              {swatch}
              <div className="flex flex-row items-center font-mono text-sm leading-none">
                <span className="text-black/50 select-none">#</span>
                <input
                  id={hexInputId}
                  type="text"
                  value={hexText}
                  onChange={handleHexInputChange}
                  onBlur={handleHexInputBlur}
                  disabled={disabled}
                  spellCheck={false}
                  className="outline-none bg-transparent text-black ml-0.5"
                  style={{ width: '6ch' }}
                />
              </div>
              {(label)
                ? (
                    <label
                      htmlFor={hexInputId}
                      className="absolute -top-2 left-2 px-1 text-xs bg-white text-nowrap text-black/60"
                    >
                      {label}
                    </label>
                  )
                : null}
            </div>
          )
        : swatch}

      { popupVisible
        ? (
            <FullPopup
              onClose={cancel}
              titleBar={false}
              customSize
            >
              <Column
                className="pt-3"
                crossStart
              >
                <Row fullw between start>
                  <CardTitle level={1} title={translate('colorSelection')} />
                  <div className="flex-1" />
                  {(colorPicked === undefined) && (
                    <div className="italic">{`(${translate('noSelection')})`}</div>
                  )}
                </Row>

                <div className="flex flex-row items-stretch gap-4">
                  <SketchPicker
                    color={(colorPicked === undefined) ? '#FFFFFF' : colorPicked}
                    onChange={handleChange}
                    onChangeComplete={handleChangeComplete}
                    width={300}
                    styles={{
                      default: {
                        picker: {
                          boxShadow: 'none',
                          padding: '0',
                        },
                      },
                    }}
                  />

                  {(hasPaletteRows)
                    ? (
                        <>
                          <Divider vertical />
                          <Column gap={8} crossStart className="w-[280px]">
                            {suggestedPaletteRows.map((row, idx) => (
                              <div
                                key={`row-${idx}`}
                                className="flex flex-col gap-1"
                              >
                                {(row.label)
                                  ? (
                                      <div className="text-xs text-black/60">
                                        {row.label}
                                      </div>
                                    )
                                  : null}
                                <div className="flex flex-row flex-wrap gap-1.5">
                                  {row.colors.map((color) => (
                                    <button
                                      key={color}
                                      type="button"
                                      onClick={() => setColorPicked(color)}
                                      aria-label={color}
                                      className="w-6 h-6 rounded-sm cursor-pointer"
                                      style={{
                                        backgroundColor: color,
                                        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)',
                                      }}
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}

                            {(previewImgUrl)
                              ? (
                                  <div
                                    className="w-full rounded-md flex items-center justify-center p-3 border border-pxMainOutline"
                                    style={{ backgroundColor: previewBg }}
                                  >
                                    <img
                                      src={previewImgUrl}
                                      alt=""
                                      className="max-w-full max-h-[120px] object-contain"
                                    />
                                  </div>
                                )
                              : null}
                          </Column>
                        </>
                      )
                    : null}
                </div>

                <Row fullw gap={8}>
                  <Button
                    imgSrc="/images/svg/gui/back.svg"
                    onClick={cancel}
                    neutralColor
                  />
                  {(canBeCleared)
                    ? (
                        <Button
                          imgSrc="/images/svg/gui/trash.svg"
                          onClick={clear}
                          criticalColor
                        />
                      )
                    : null}
                  <Button
                    imgSrc="/images/svg/gui/check.svg"
                    onClick={done}
                    disabled={(!canBeCleared) && (!colorPicked)}
                    baseColor
                  />
                </Row>
              </Column>

            </FullPopup>
          )
        : null }

    </div>
  );
}

ColorPicker.propTypes = {
  id: PropTypes.string,
  colorPickingDone: PropTypes.func,
  disabled: PropTypes.bool,
  initialColor: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      r: PropTypes.string,
      g: PropTypes.string,
      b: PropTypes.string,
      a: PropTypes.string,
    }),
  ]),
  canBeCleared: PropTypes.bool,
  displayHexInput: PropTypes.bool,
  label: PropTypes.string,
  suggested: PropTypes.shape({
    palettes: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        colors: PropTypes.arrayOf(PropTypes.string).isRequired,
      }),
    ),
    imgUrl: PropTypes.string,
  }),
};

export default ColorPicker;
